import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, FormEvent } from "react";
import {
  User, Mail, Building2, Briefcase, ShieldCheck, Lock, Eye, EyeOff,
  CheckCircle2, KeyRound, BadgeCheck, HeartPulse,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { ROLE_LABELS, type AppRole } from "@/lib/roles";
import { changePassword } from "@/integrations/api/auth";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/profile")({
  component: ProfilePage,
});

const ROLE_COLORS: Partial<Record<AppRole, string>> = {
  super_admin: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  biomed_head: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  biomed_engineer: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300",
  facility_manager: "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300",
  finance: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  purchase_stores: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  hod: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  dept_user: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  quality: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300",
  management: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300",
};

function InfoRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border/50 last:border-0">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm font-medium truncate">{value || "—"}</div>
      </div>
    </div>
  );
}

function AvatarBig({ initials, photoUrl }: { initials: string; photoUrl?: string | null }) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        className="h-20 w-20 rounded-full object-cover shadow-lg ring-4 ring-primary/20"
      />
    );
  }
  return (
    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70 text-2xl font-bold text-primary-foreground shadow-lg ring-4 ring-primary/20">
      {initials}
    </div>
  );
}

function ChangePasswordCard() {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  const criteria = {
    length: newPassword.length >= 8,
    upperLower: /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword),
    number: /\d/.test(newPassword),
    special: /[^A-Za-z0-9]/.test(newPassword),
  };
  const allMet = Object.values(criteria).every(Boolean);
  const isMatch = newPassword === confirmPassword && newPassword !== "";
  const canSubmit = allMet && isMatch && !busy;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await changePassword(newPassword, confirmPassword);
      setSuccess(true);
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password updated successfully!");
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed to update password");
    } finally {
      setBusy(false);
    }
  }

  function CriteriaItem({ label, valid }: { label: string; valid: boolean }) {
    return (
      <div className={`flex items-center gap-1.5 text-xs ${valid ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
        <CheckCircle2 className={`h-3.5 w-3.5 ${valid ? "text-emerald-500" : "text-muted-foreground/30"}`} />
        {label}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4 text-primary" />
          Change Password
        </CardTitle>
      </CardHeader>
      <CardContent>
        {success ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <BadgeCheck className="h-12 w-12 text-emerald-500" />
            <p className="text-sm font-medium">Password updated successfully!</p>
            <p className="text-xs text-muted-foreground">You are still logged in. Your session continues seamlessly.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">New Password</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showNew ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background pl-9 pr-10 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground hover:text-foreground"
                >
                  {showNew ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Confirm New Password</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showConfirm ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background pl-9 pr-10 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground hover:text-foreground"
                >
                  {showConfirm ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              {confirmPassword.length > 0 && !isMatch && (
                <p className="mt-1.5 text-xs text-destructive">Passwords do not match</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-1.5 rounded-lg border border-border bg-muted/30 p-3">
              <CriteriaItem label="Min 8 characters" valid={criteria.length} />
              <CriteriaItem label="Upper & lowercase" valid={criteria.upperLower} />
              <CriteriaItem label="At least one number" valid={criteria.number} />
              <CriteriaItem label="Special character" valid={criteria.special} />
            </div>

            <Button type="submit" disabled={!canSubmit} className="w-full">
              {busy ? "Updating..." : "Update Password"}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function ProfilePage() {
  const { data } = useCurrentUser();

  const fullName = data?.profile?.full_name ?? null;
  const email = data?.profile?.email ?? data?.user?.email ?? null;
  const roles = (data?.roles ?? []) as AppRole[];
  const branches = data?.branches ?? [];

  const initials = (fullName || email || "U")
    .split(/\s+|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  // Find the user's home branch name
  const homeBranch = branches.find((b) => b.id === data?.profile?.branch_id);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">My Profile</h1>
        <p className="text-sm text-muted-foreground">View your account details and manage your security settings.</p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* LEFT COLUMN */}
        <div className="space-y-6 md:col-span-1">
          {/* Avatar + name card */}
          <Card>
            <CardContent className="flex flex-col items-center gap-3 pt-6 pb-5 text-center">
              <div className="relative">
                <AvatarBig initials={initials} photoUrl={data?.profile?.avatar_url} />
                <span className="absolute -bottom-1 -right-1 rounded-full bg-background p-1 shadow-sm ring-1 ring-border">
                  <HeartPulse className="h-3.5 w-3.5 text-primary" />
                </span>
              </div>
              <div>
                <div className="font-semibold">{fullName || "—"}</div>
                <div className="text-xs text-muted-foreground">{email}</div>
              </div>
              <div className="flex flex-wrap justify-center gap-1.5 pt-1">
                {roles.map((r) => (
                  <span
                    key={r}
                    className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${ROLE_COLORS[r] ?? "bg-muted text-muted-foreground"}`}
                  >
                    {ROLE_LABELS[r] || r}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Access info */}
          <Card>
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-sm">
                <ShieldCheck className="h-4 w-4 text-primary" /> Access & Roles
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-1">
                <div className="py-3 border-b border-border/50">
                  <div className="text-xs text-muted-foreground mb-1.5">Assigned Roles</div>
                  <div className="flex flex-wrap gap-1">
                    {roles.length > 0 ? roles.map((r) => (
                      <Badge key={r} variant="secondary" className="text-xs">
                        {ROLE_LABELS[r] || r}
                      </Badge>
                    )) : <span className="text-sm text-muted-foreground">No roles assigned</span>}
                  </div>
                </div>
                <div className="py-3">
                  <div className="text-xs text-muted-foreground mb-1">Home Branch</div>
                  <div className="text-sm font-medium flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    {homeBranch?.name ?? "—"}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6 md:col-span-2">
          {/* Account details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-4 w-4 text-primary" />
                Account Details
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <InfoRow icon={User} label="Full Name" value={fullName} />
              <InfoRow icon={Mail} label="Email Address" value={email} />
              <InfoRow icon={Building2} label="Home Branch" value={homeBranch?.name} />
              <InfoRow icon={Briefcase} label="Roles" value={roles.map((r) => ROLE_LABELS[r] || r).join(", ")} />
            </CardContent>
          </Card>

          {/* Change password */}
          <ChangePasswordCard />
        </div>
      </div>
    </div>
  );
}
